import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import {
  Cpu, Key, RotateCw, Power, Plus, Activity, AlertTriangle, Heart,
  Webhook, ScrollText, Copy, Eye, Download, Hash, CheckCircle2, XCircle,
  Loader2, Search, RefreshCw, Package,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
const ADMIN_BASE = `${API_URL}/obd-sdk/admin`;
const ADMIN_ROLES = new Set(['CEO', 'CTO', 'ADMIN', 'SUPER_ADMIN']);

// ── Helpers ──────────────────────────────────────────────────────────
const fmtDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' });
};

const copy = (text, label = 'Copied') => {
  navigator.clipboard.writeText(text);
  toast.success(`${label} to clipboard`);
};

const sevColor = (sev) => {
  const s = (sev || '').toUpperCase();
  if (s === 'CRITICAL') return 'bg-red-100 text-red-700 border-red-200';
  if (s === 'MINOR')    return 'bg-amber-100 text-amber-700 border-amber-200';
  return 'bg-gray-100 text-gray-600 border-gray-200';
};

// ── Main Page ────────────────────────────────────────────────────────
export default function ObdSdkDashboard() {
  const { user } = useAuth();
  const [tab, setTab] = useState('overview');

  const userRole = (user?.role_code || '').toUpperCase();
  const userRoles = new Set((user?.roles || []).map((r) => (r.code || '').toUpperCase()));
  const isAdmin = ADMIN_ROLES.has(userRole) || [...userRoles].some((r) => ADMIN_ROLES.has(r));

  if (!user) {
    return <div className="p-8 text-gray-500">Loading…</div>;
  }
  if (!isAdmin) {
    return (
      <div className="p-12 text-center" data-testid="obd-sdk-access-denied">
        <AlertTriangle className="mx-auto h-10 w-10 text-amber-500" />
        <h2 className="mt-3 text-lg font-semibold text-gray-700">Admin access required</h2>
        <p className="mt-1 text-sm text-gray-500">
          The OBD SDK dashboard is restricted to CEO, CTO and Admin roles.
        </p>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="obd-sdk-dashboard">
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2">
            <Cpu className="h-6 w-6 text-indigo-600" />
            <h1 className="text-2xl font-semibold tracking-tight">OBD SDK</h1>
            <Badge variant="outline" className="ml-2">B2B · Cloud-Powered</Badge>
          </div>
          <p className="text-sm text-gray-500 mt-1">
            Partner management, scan inventory and delivery for the WiseDrive OBD SDK.
          </p>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab} className="w-full">
        <TabsList data-testid="obd-sdk-tabs">
          <TabsTrigger value="overview"  data-testid="tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="partners"  data-testid="tab-partners">Partners</TabsTrigger>
          <TabsTrigger value="scans"     data-testid="tab-scans">Scans</TabsTrigger>
          <TabsTrigger value="webhooks"  data-testid="tab-webhooks">Webhooks</TabsTrigger>
          <TabsTrigger value="audit"     data-testid="tab-audit">Audit</TabsTrigger>
        </TabsList>

        <TabsContent value="overview"><OverviewTab /></TabsContent>
        <TabsContent value="partners"><PartnersTab /></TabsContent>
        <TabsContent value="scans"><ScansTab /></TabsContent>
        <TabsContent value="webhooks"><WebhooksTab /></TabsContent>
        <TabsContent value="audit"><AuditTab /></TabsContent>
      </Tabs>
    </div>
  );
}

// ── Overview Tab ─────────────────────────────────────────────────────
function OverviewTab() {
  const [stats, setStats] = useState(null);
  const [usage, setUsage] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, u] = await Promise.all([
        axios.get(`${ADMIN_BASE}/stats/overview`),
        axios.get(`${ADMIN_BASE}/stats/usage?days=14`),
      ]);
      setStats(s.data);
      setUsage(u.data.series || []);
    } catch (e) {
      toast.error('Failed to load stats');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading || !stats) {
    return <div className="py-12 text-center text-gray-500"><Loader2 className="inline h-5 w-5 animate-spin" /> Loading…</div>;
  }

  const maxCount = Math.max(1, ...usage.map((r) => r.count));

  const cards = [
    { label: 'Scans today',         value: stats.scans.today,              icon: Activity,      color: 'text-indigo-600' },
    { label: 'Scans (7 days)',      value: stats.scans.last_7d,            icon: Activity,      color: 'text-blue-600' },
    { label: 'Critical rate (7d)',  value: `${stats.scans.critical_rate_pct}%`, icon: AlertTriangle, color: 'text-red-600' },
    { label: 'Avg health (7d)',     value: stats.avg_health_score_7d ?? '—', icon: Heart,       color: 'text-emerald-600' },
    { label: 'Partners',            value: `${stats.partners.active}/${stats.partners.total} active`, icon: Cpu,    color: 'text-slate-600' },
  ];

  return (
    <div className="space-y-5 mt-4">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {cards.map((c) => (
          <Card key={c.label} data-testid={`stat-card-${c.label.toLowerCase().replace(/[^a-z]/g,'-')}`}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <p className="text-xs text-gray-500 uppercase tracking-wide">{c.label}</p>
                <c.icon className={`h-4 w-4 ${c.color}`} />
              </div>
              <p className="text-2xl font-semibold mt-2">{c.value}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center justify-between">
            <span>Scans per day (last 14 days)</span>
            <Button variant="ghost" size="sm" onClick={load}><RefreshCw className="h-3.5 w-3.5 mr-1" /> Refresh</Button>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-end gap-1 h-40 border-l border-b border-gray-200 pl-2 pr-2 pb-1" data-testid="usage-chart">
            {usage.map((r) => {
              const h = Math.max(2, (r.count / maxCount) * 130);
              const hc = (r.critical / maxCount) * 130;
              return (
                <div key={r.date} className="flex-1 flex flex-col items-center justify-end group relative">
                  <div className="absolute bottom-full mb-1 hidden group-hover:block whitespace-nowrap text-[11px] bg-slate-900 text-white px-1.5 py-0.5 rounded">
                    {r.date} · {r.count} scans{r.critical ? ` · ${r.critical} critical` : ''}
                  </div>
                  <div className="relative w-full bg-indigo-500 rounded-t" style={{ height: `${h}px` }}>
                    {r.critical > 0 && (
                      <div className="absolute bottom-0 w-full bg-red-600 rounded-t"
                           style={{ height: `${hc}px` }} />
                    )}
                  </div>
                  <span className="text-[10px] text-gray-500 mt-1">{r.date.slice(-5)}</span>
                </div>
              );
            })}
          </div>
          <div className="flex gap-3 mt-3 text-xs text-gray-500">
            <span className="flex items-center gap-1"><span className="w-3 h-3 bg-indigo-500 rounded-sm" /> Scans</span>
            <span className="flex items-center gap-1"><span className="w-3 h-3 bg-red-600 rounded-sm" /> With critical DTCs</span>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ── Partners Tab ─────────────────────────────────────────────────────
function PartnersTab() {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ partner_id: '', name: '', key_type: 'live' });
  const [newKey, setNewKey] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${ADMIN_BASE}/partners`);
      setPartners(r.data.partners || []);
    } catch { toast.error('Failed to load partners'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const createPartner = async () => {
    if (!form.partner_id || !form.name) return toast.error('Fill partner_id and name');
    try {
      const r = await axios.post(`${ADMIN_BASE}/partners`, form);
      setNewKey(r.data);
      setCreating(false);
      setForm({ partner_id: '', name: '', key_type: 'live' });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Create failed'); }
  };

  const toggle = async (p) => {
    try {
      await axios.patch(`${ADMIN_BASE}/partners/${p.partner_id}`, { active: !p.active });
      toast.success(`Partner ${p.active ? 'disabled' : 'enabled'}`);
      load();
    } catch { toast.error('Toggle failed'); }
  };

  const rotate = async (p) => {
    if (!window.confirm(`Rotate API key for ${p.partner_id}? Old key will stop working.`)) return;
    try {
      const r = await axios.post(`${ADMIN_BASE}/partners/${p.partner_id}/rotate-key`);
      setNewKey(r.data);
      load();
    } catch { toast.error('Rotate failed'); }
  };

  return (
    <div className="mt-4 space-y-4">
      <div className="flex items-center justify-between gap-2">
        <div className="text-sm text-gray-500">
          {partners.length} partner{partners.length !== 1 ? 's' : ''} · click
          <Package className="inline h-3.5 w-3.5 mx-1" />
          to download the latest integration package (ZIP for MFCWL handoff).
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={async () => {
              try {
                const r = await axios.get(`${ADMIN_BASE}/integration-package`, { responseType: 'blob' });
                const cd = r.headers['content-disposition'] || '';
                const m = cd.match(/filename="?([^"]+)"?/);
                const filename = m ? m[1] : 'MFCWL_Integration_Package.zip';
                const url = URL.createObjectURL(new Blob([r.data], { type: 'application/zip' }));
                const a = document.createElement('a');
                a.href = url; a.download = filename; a.click();
                URL.revokeObjectURL(url);
                toast.success('Integration package downloaded');
              } catch (e) {
                toast.error(e.response?.data?.detail || 'Download failed — package not built on server');
              }
            }}
            data-testid="download-package-btn"
          >
            <Package className="h-4 w-4 mr-1" /> Download integration package
          </Button>
          <Button onClick={() => setCreating(true)} data-testid="create-partner-btn">
            <Plus className="h-4 w-4 mr-1" /> New partner
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="py-12 text-center text-gray-500"><Loader2 className="inline h-5 w-5 animate-spin" /></div>
      ) : (
        <div className="border rounded-lg overflow-hidden" data-testid="partners-table">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-600 text-xs uppercase tracking-wide">
              <tr>
                <th className="text-left px-4 py-2">Partner ID</th>
                <th className="text-left px-4 py-2">Name</th>
                <th className="text-left px-4 py-2">Type</th>
                <th className="text-right px-4 py-2">Scans</th>
                <th className="text-left px-4 py-2">Last scan</th>
                <th className="text-left px-4 py-2">Status</th>
                <th className="text-right px-4 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {partners.length === 0 ? (
                <tr><td colSpan={7} className="text-center py-8 text-gray-400">No partners yet</td></tr>
              ) : partners.map((p) => (
                <tr key={p.partner_id} className="border-t hover:bg-gray-50" data-testid={`partner-row-${p.partner_id}`}>
                  <td className="px-4 py-2 font-mono text-xs">{p.partner_id}</td>
                  <td className="px-4 py-2">{p.name}</td>
                  <td className="px-4 py-2">
                    <Badge variant={p.key_type === 'test' ? 'secondary' : 'default'}>{p.key_type || 'live'}</Badge>
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums">{p.scan_count}</td>
                  <td className="px-4 py-2 text-gray-500 text-xs">{fmtDate(p.last_scan_at)}</td>
                  <td className="px-4 py-2">
                    {p.active
                      ? <span className="inline-flex items-center gap-1 text-green-700"><CheckCircle2 className="h-3.5 w-3.5" /> active</span>
                      : <span className="inline-flex items-center gap-1 text-gray-500"><XCircle className="h-3.5 w-3.5" /> disabled</span>}
                  </td>
                  <td className="px-4 py-2 text-right space-x-1">
                    <Button size="sm" variant="ghost" onClick={() => rotate(p)} data-testid={`rotate-${p.partner_id}`}>
                      <RotateCw className="h-3.5 w-3.5" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => toggle(p)} data-testid={`toggle-${p.partner_id}`}>
                      <Power className="h-3.5 w-3.5" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create partner dialog */}
      <Dialog open={creating} onOpenChange={setCreating}>
        <DialogContent data-testid="create-partner-dialog">
          <DialogHeader><DialogTitle>New partner</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs text-gray-500">Partner ID (lowercase, no spaces)</label>
              <Input value={form.partner_id}
                     onChange={(e) => setForm({ ...form, partner_id: e.target.value.toLowerCase().replace(/\s+/g,'_') })}
                     placeholder="acme_motors" data-testid="partner-id-input" />
            </div>
            <div>
              <label className="text-xs text-gray-500">Display name</label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                     placeholder="Acme Motors Ltd" data-testid="partner-name-input" />
            </div>
            <div>
              <label className="text-xs text-gray-500">Key type</label>
              <Select value={form.key_type} onValueChange={(v) => setForm({ ...form, key_type: v })}>
                <SelectTrigger data-testid="partner-key-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="live">Production (wds_live_…)</SelectItem>
                  <SelectItem value="test">Sandbox (wds_test_…)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreating(false)}>Cancel</Button>
            <Button onClick={createPartner} data-testid="confirm-create-partner">Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* API key shown ONCE */}
      <Dialog open={!!newKey} onOpenChange={() => setNewKey(null)}>
        <DialogContent data-testid="new-key-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Key className="h-4 w-4" /> New API key</DialogTitle>
          </DialogHeader>
          {newKey && (
            <div className="space-y-3">
              <div className="rounded-md bg-amber-50 border border-amber-200 p-3 text-sm text-amber-800">
                <b>Store this key securely.</b> It will never be shown again.
              </div>
              <div>
                <div className="text-xs text-gray-500 mb-1">Partner</div>
                <div className="font-mono text-sm">{newKey.partner_id}</div>
              </div>
              <div>
                <div className="text-xs text-gray-500 mb-1">API key</div>
                <div className="flex items-center gap-2">
                  <code className="flex-1 text-xs bg-gray-900 text-gray-100 p-2 rounded break-all">{newKey.api_key}</code>
                  <Button size="sm" variant="outline" onClick={() => copy(newKey.api_key, 'API key')}>
                    <Copy className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setNewKey(null)}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ── Scans Tab ────────────────────────────────────────────────────────
function ScansTab() {
  const [scans, setScans] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [partner, setPartner] = useState('');
  const [inspection, setInspection] = useState('');
  const [critical, setCritical] = useState('all');
  const [openId, setOpenId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: '100', days: '30' });
      if (partner)    params.set('partner_id', partner);
      if (inspection) params.set('inspection_id', inspection);
      if (critical !== 'all') params.set('has_critical', critical);
      const r = await axios.get(`${ADMIN_BASE}/scans?${params}`);
      setScans(r.data.scans || []); setTotal(r.data.total || 0);
    } catch { toast.error('Failed to load scans'); }
    finally { setLoading(false); }
  }, [partner, inspection, critical]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="mt-4 space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <Input placeholder="Filter by partner_id"
               value={partner} onChange={(e) => setPartner(e.target.value)}
               className="w-52" data-testid="filter-partner" />
        <Input placeholder="Filter by inspection_id"
               value={inspection} onChange={(e) => setInspection(e.target.value)}
               className="w-64" data-testid="filter-inspection" />
        <Select value={critical} onValueChange={setCritical}>
          <SelectTrigger className="w-48" data-testid="filter-critical"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All scans</SelectItem>
            <SelectItem value="true">With critical DTCs</SelectItem>
            <SelectItem value="false">Clean or minor only</SelectItem>
          </SelectContent>
        </Select>
        <Button variant="outline" onClick={load}><Search className="h-4 w-4 mr-1" /> Apply</Button>
        <span className="text-sm text-gray-500 ml-auto">{total} total</span>
      </div>

      {loading ? (
        <div className="py-12 text-center text-gray-500"><Loader2 className="inline h-5 w-5 animate-spin" /></div>
      ) : (
        <div className="border rounded-lg overflow-x-auto" data-testid="scans-table">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-600 text-xs uppercase tracking-wide">
              <tr>
                <th className="text-left px-4 py-2">Scan ID</th>
                <th className="text-left px-4 py-2">Partner</th>
                <th className="text-left px-4 py-2">Inspection</th>
                <th className="text-left px-4 py-2">Vehicle</th>
                <th className="text-right px-4 py-2">DTCs</th>
                <th className="text-right px-4 py-2">Critical</th>
                <th className="text-right px-4 py-2">Health</th>
                <th className="text-left px-4 py-2">When</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {scans.length === 0 ? (
                <tr><td colSpan={9} className="text-center py-8 text-gray-400">No scans match the filter</td></tr>
              ) : scans.map((s) => {
                const v = s.vehicle || {};
                const sum = s.summary || {};
                return (
                  <tr key={s.scan_id} className="border-t hover:bg-indigo-50 cursor-pointer"
                      onClick={() => setOpenId(s.scan_id)} data-testid={`scan-row-${s.scan_id}`}>
                    <td className="px-4 py-2 font-mono text-xs">{s.scan_id}</td>
                    <td className="px-4 py-2">{s.partner_id}</td>
                    <td className="px-4 py-2 font-mono text-xs">{s.inspection_id}</td>
                    <td className="px-4 py-2 text-xs">{v.make} {v.model} {v.year || ''}</td>
                    <td className="px-4 py-2 text-right tabular-nums">{sum.total ?? '—'}</td>
                    <td className="px-4 py-2 text-right">
                      {sum.critical > 0 ? (
                        <Badge variant="destructive">{sum.critical}</Badge>
                      ) : <span className="text-gray-400">0</span>}
                    </td>
                    <td className="px-4 py-2 text-right tabular-nums">{sum.health_score ?? '—'}</td>
                    <td className="px-4 py-2 text-gray-500 text-xs">{fmtDate(s.completed_at || s.created_at)}</td>
                    <td className="px-2 py-2"><Eye className="h-4 w-4 text-gray-400" /></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <ScanDetailDrawer scanId={openId} onClose={() => setOpenId(null)} />
    </div>
  );
}

// ── Scan detail drawer ───────────────────────────────────────────────
function ScanDetailDrawer({ scanId, onClose }) {
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!scanId) return;
    setLoading(true);
    axios.get(`${ADMIN_BASE}/scans/${scanId}`)
      .then((r) => setDoc(r.data))
      .catch(() => toast.error('Failed to load scan'))
      .finally(() => setLoading(false));
  }, [scanId]);

  const download = () => {
    const blob = new Blob([JSON.stringify(doc, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `${scanId}.json`; a.click();
    URL.revokeObjectURL(url);
  };

  const buckets = doc?.dtc || {};
  const renderDtc = (dtc) => (
    <div key={dtc.code} className="border rounded p-2 bg-white">
      <div className="flex items-center justify-between">
        <span className="font-mono text-sm font-semibold">{dtc.code}</span>
        <Badge className={sevColor(dtc.severity)}>{dtc.severity || '—'}</Badge>
      </div>
      {dtc.description && <p className="text-xs text-gray-600 mt-1">{dtc.description}</p>}
      {dtc.ai_insights && (
        <div className="mt-2 space-y-1 text-xs">
          {dtc.ai_insights.possible_causes?.length > 0 && (
            <div><b>Causes:</b> {dtc.ai_insights.possible_causes.slice(0,3).join(' · ')}</div>
          )}
          {dtc.ai_insights.symptoms?.length > 0 && (
            <div><b>Symptoms:</b> {dtc.ai_insights.symptoms.slice(0,3).join(' · ')}</div>
          )}
          {dtc.ai_insights.recommended_fixes?.length > 0 && (
            <div><b>Fixes:</b> {dtc.ai_insights.recommended_fixes.slice(0,3).join(' · ')}</div>
          )}
          {dtc.ai_insights.est_repair_inr && (
            <div className="text-emerald-700"><b>Repair ₹:</b> {dtc.ai_insights.est_repair_inr}</div>
          )}
        </div>
      )}
    </div>
  );

  return (
    <Sheet open={!!scanId} onOpenChange={onClose}>
      <SheetContent className="w-[720px] sm:max-w-[720px] overflow-y-auto" data-testid="scan-drawer">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2 font-mono text-base">
            <Hash className="h-4 w-4" /> {scanId}
          </SheetTitle>
        </SheetHeader>
        {loading || !doc ? (
          <div className="py-16 text-center text-gray-500"><Loader2 className="inline h-5 w-5 animate-spin" /></div>
        ) : (
          <div className="space-y-4 mt-4 text-sm">
            <div className="flex items-center justify-between">
              <div className="flex gap-2 items-center flex-wrap">
                <Badge>{doc.status}</Badge>
                <Badge variant="outline">partner: {doc.partner_id}</Badge>
                <Badge variant="outline">profile: {doc.profile_id}</Badge>
              </div>
              <Button size="sm" variant="outline" onClick={download} data-testid="download-json">
                <Download className="h-3.5 w-3.5 mr-1" /> JSON
              </Button>
            </div>

            {/* Summary */}
            {doc.summary && (
              <Card>
                <CardContent className="p-3">
                  <div className="grid grid-cols-4 gap-2 text-center text-xs">
                    <div><p className="text-gray-500">Total</p><p className="text-lg font-semibold">{doc.summary.total}</p></div>
                    <div><p className="text-gray-500">Critical</p><p className="text-lg font-semibold text-red-600">{doc.summary.critical}</p></div>
                    <div><p className="text-gray-500">Health</p><p className="text-lg font-semibold text-emerald-600">{doc.summary.health_score}</p></div>
                    <div className="flex items-center justify-center text-xs text-gray-500 italic">7-day engine</div>
                  </div>
                  {doc.summary.ai_verdict && (
                    <p className="mt-2 text-xs text-slate-700 italic border-t pt-2">“{doc.summary.ai_verdict}”</p>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Vehicle */}
            {doc.vehicle && (
              <Card><CardContent className="p-3 text-xs">
                <div className="font-semibold text-sm mb-1">Vehicle</div>
                <pre className="bg-gray-50 p-2 rounded text-[11px] overflow-x-auto">
{JSON.stringify(doc.vehicle, null, 2)}
                </pre>
              </CardContent></Card>
            )}

            {/* DTC buckets */}
            {['active','pending','permanent','history'].map((b) => {
              const arr = buckets[b] || [];
              return (
                <Card key={b}><CardContent className="p-3">
                  <div className="flex items-center justify-between mb-2">
                    <div className="font-semibold text-sm capitalize">{b}</div>
                    <Badge variant="outline">{arr.length}</Badge>
                  </div>
                  {arr.length === 0 ? (
                    <p className="text-xs text-gray-400 italic">No codes</p>
                  ) : (
                    <div className="space-y-2">{arr.map(renderDtc)}</div>
                  )}
                </CardContent></Card>
              );
            })}

            {/* Raw responses (collapsible) */}
            <details className="bg-gray-50 rounded p-3">
              <summary className="cursor-pointer text-xs font-semibold text-gray-700">
                Raw ELM327 responses ({doc.raw_responses?.length || 0} frames)
              </summary>
              <pre className="mt-2 text-[10px] overflow-x-auto whitespace-pre-wrap">
{JSON.stringify(doc.raw_responses || [], null, 2)}
              </pre>
            </details>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

// ── Webhooks Tab ─────────────────────────────────────────────────────
function WebhooksTab() {
  const [hooks, setHooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('');
  const [form, setForm] = useState({ partner_id: '', url: '' });
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${ADMIN_BASE}/webhooks`);
      setHooks(r.data.webhooks || []); setStatus(r.data.delivery_engine_status || '');
    } catch { toast.error('Failed to load webhooks'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!form.partner_id || !form.url) return toast.error('Fill partner_id and URL');
    try {
      await axios.post(`${ADMIN_BASE}/webhooks`, { ...form, events: ['scan.completed'] });
      toast.success('Webhook registered');
      setCreating(false); setForm({ partner_id: '', url: '' }); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Create failed'); }
  };

  const del = async (id) => {
    if (!window.confirm('Delete this webhook?')) return;
    try { await axios.delete(`${ADMIN_BASE}/webhooks/${id}`); toast.success('Deleted'); load(); }
    catch { toast.error('Delete failed'); }
  };

  return (
    <div className="mt-4 space-y-4">
      {status && (
        <div className="rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-800 flex items-center gap-2">
          <AlertTriangle className="h-3.5 w-3.5" /> {status}
        </div>
      )}

      <div className="flex justify-end">
        <Button onClick={() => setCreating(true)} data-testid="create-webhook-btn">
          <Plus className="h-4 w-4 mr-1" /> Register webhook
        </Button>
      </div>

      {loading ? (
        <div className="py-12 text-center text-gray-500"><Loader2 className="inline h-5 w-5 animate-spin" /></div>
      ) : (
        <div className="border rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-600 text-xs uppercase tracking-wide">
              <tr>
                <th className="text-left px-4 py-2">Partner</th>
                <th className="text-left px-4 py-2">URL</th>
                <th className="text-left px-4 py-2">Events</th>
                <th className="text-left px-4 py-2">Created</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {hooks.length === 0 ? (
                <tr><td colSpan={5} className="text-center py-8 text-gray-400">No webhooks registered</td></tr>
              ) : hooks.map((h) => (
                <tr key={h.id} className="border-t hover:bg-gray-50" data-testid={`webhook-row-${h.id}`}>
                  <td className="px-4 py-2 font-mono text-xs">{h.partner_id}</td>
                  <td className="px-4 py-2 text-xs break-all">{h.url}</td>
                  <td className="px-4 py-2 text-xs">{(h.events || []).join(', ')}</td>
                  <td className="px-4 py-2 text-gray-500 text-xs">{fmtDate(h.created_at)}</td>
                  <td className="px-2 py-2 text-right">
                    <Button size="sm" variant="ghost" onClick={() => del(h.id)}><Power className="h-3.5 w-3.5" /></Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={creating} onOpenChange={setCreating}>
        <DialogContent>
          <DialogHeader><DialogTitle>Register webhook</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input placeholder="partner_id"
                   value={form.partner_id}
                   onChange={(e) => setForm({ ...form, partner_id: e.target.value })}
                   data-testid="webhook-partner-input" />
            <Input placeholder="https://partner.example.com/wisedrive-hook"
                   value={form.url}
                   onChange={(e) => setForm({ ...form, url: e.target.value })}
                   data-testid="webhook-url-input" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreating(false)}>Cancel</Button>
            <Button onClick={create} data-testid="confirm-create-webhook">Register</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ── Audit Tab ────────────────────────────────────────────────────────
function AuditTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionFilter, setActionFilter] = useState('all');
  const actions = ['all','partner.create','partner.toggle','partner.rotate_key','scan.view','webhook.create','webhook.delete'];

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: '200' });
      if (actionFilter !== 'all') params.set('action', actionFilter);
      const r = await axios.get(`${ADMIN_BASE}/audit?${params}`);
      setRows(r.data.entries || []);
    } catch { toast.error('Failed to load audit log'); }
    finally { setLoading(false); }
  }, [actionFilter]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="mt-4 space-y-3">
      <div className="flex items-center gap-2">
        <Select value={actionFilter} onValueChange={setActionFilter}>
          <SelectTrigger className="w-64"><SelectValue /></SelectTrigger>
          <SelectContent>
            {actions.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button variant="ghost" size="sm" onClick={load}><RefreshCw className="h-3.5 w-3.5 mr-1" /> Refresh</Button>
      </div>

      {loading ? (
        <div className="py-12 text-center text-gray-500"><Loader2 className="inline h-5 w-5 animate-spin" /></div>
      ) : (
        <div className="border rounded-lg overflow-hidden" data-testid="audit-table">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-600 text-xs uppercase tracking-wide">
              <tr>
                <th className="text-left px-4 py-2">Action</th>
                <th className="text-left px-4 py-2">Actor</th>
                <th className="text-left px-4 py-2">Details</th>
                <th className="text-left px-4 py-2">When</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr><td colSpan={4} className="text-center py-8 text-gray-400">No audit entries</td></tr>
              ) : rows.map((r) => (
                <tr key={r.id} className="border-t">
                  <td className="px-4 py-2 font-mono text-xs">{r.action}</td>
                  <td className="px-4 py-2 text-xs">{r.actor_email || r.actor_id}</td>
                  <td className="px-4 py-2 text-xs text-gray-600">{JSON.stringify(r.details || {})}</td>
                  <td className="px-4 py-2 text-gray-500 text-xs">{fmtDate(r.ts)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
